<template>
  <div class="minimal-cta" :class="ctaClasses">
    <div class="minimal-cta-content">
      <span class="minimal-cta-text">{{ displayText }}</span>
      <s-button
        :btn-link="href"
        :class="buttonClasses"
        class="minimal-cta-button"
        :icon-font="iconPosition === 'left' ? buttonIcon : ''"
        :icon-font-after="iconPosition === 'right' ? buttonIcon : ''"
      >
        {{ buttonText }}
      </s-button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import SButton from './SButton.vue'

const props = defineProps({
  text: {
    type: String,
    default: 'Ready to get started?'
  },
  buttonText: {
    type: String,
    default: 'Get Started'
  },
  href: {
    type: String,
    default: '/pricing'
  },
  buttonVariant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'outline', 'ghost'].includes(value)
  },
  buttonSize: {
    type: String,
    default: 'small',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  buttonIcon: {
    type: String,
    default: 's-icon-arrow-right'
  },
  iconPosition: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right'].includes(value)
  },
  align: {
    type: String,
    default: 'space-between',
    validator: (value) => ['left', 'center', 'right', 'space-between'].includes(value)
  },
  background: {
    type: String,
    default: 'light',
    validator: (value) => ['light', 'dark', 'accent', 'transparent'].includes(value)
  }
})

const displayText = computed(() => props.text)

const ctaClasses = computed(() => {
  return [
    `minimal-cta--align-${props.align}`,
    `minimal-cta--bg-${props.background}`
  ]
})

const buttonClasses = computed(() => {
  return [
    's-button',
    `s-button-${props.buttonVariant}`,
    `s-button-${props.buttonSize}`
  ]
})
</script>

<style scoped>
.minimal-cta {
  padding: 1.25rem 1.75rem;
  border-radius: 12px;
  margin: 2.5rem 0;
  transition: all 0.3s ease;
  position: relative;
}

.minimal-cta-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.minimal-cta-text {
  font-size: 1.125rem;
  font-weight: 500;
  flex: 1;
  min-width: 200px;
  line-height: 1.5;
  letter-spacing: -0.01em;
}

.minimal-cta-button {
  flex-shrink: 0;
}

/* Alignment variants */
.minimal-cta--align-left .minimal-cta-content {
  justify-content: flex-start;
}

.minimal-cta--align-center .minimal-cta-content {
  justify-content: center;
  text-align: center;
}

.minimal-cta--align-center .minimal-cta-text {
  flex: initial;
}

.minimal-cta--align-right .minimal-cta-content {
  justify-content: flex-end;
}

.minimal-cta--align-space-between .minimal-cta-content {
  justify-content: space-between;
}

/* Background variants with better visual prominence */
.minimal-cta--bg-light {
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.minimal-cta--bg-light .minimal-cta-text {
  color: #212529;
  font-weight: 600;
}

.minimal-cta--bg-dark {
  background: linear-gradient(180deg, #2b2d30 0%, #212529 100%);
  border: 1px solid #343a40;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.minimal-cta--bg-dark .minimal-cta-text {
  color: #f8f9fa;
  font-weight: 600;
}

.minimal-cta--bg-accent {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.25);
  position: relative;
  overflow: hidden;
}

.minimal-cta--bg-accent::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 60%);
  pointer-events: none;
}

.minimal-cta--bg-accent .minimal-cta-text {
  color: white;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.minimal-cta--bg-transparent {
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 0.75rem 1.25rem;
}

/* Hover effects */
.minimal-cta:hover {
  transform: translateY(-2px);
}

.minimal-cta--bg-light:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border-color: #adb5bd;
}

.minimal-cta--bg-dark:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.minimal-cta--bg-accent:hover {
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.35);
}

.minimal-cta--bg-transparent:hover {
  transform: none;
  background: rgba(255, 255, 255, 0.7);
  border-color: rgba(0, 0, 0, 0.12);
}

/* Mobile responsive with better tap targets */
@media (max-width: 640px) {
  .minimal-cta {
    padding: 1.5rem 1.25rem;
    margin: 2rem -1rem;
    border-radius: 0;
  }

  .minimal-cta-content {
    flex-direction: column;
    text-align: center;
    gap: 1.25rem;
  }

  .minimal-cta-text {
    width: 100%;
    text-align: center;
    font-size: 1.1875rem;
    line-height: 1.4;
  }

  .minimal-cta-button {
    width: 100%;
    min-height: 52px;
  }

  @media (max-width: 400px) {
    .minimal-cta-content {
      gap: 1rem;
    }
  }
}
</style>
